import type { ReactNode } from "react";
import { Logo } from "@/design/components";
import styles from "./AuthLayout.module.css";

interface AuthLayoutProps {
  children: ReactNode;
}

/**
 * Split auth canvas: an editorial brand panel (left) stating what Cogent is,
 * and the form surface (right). The left panel carries the product story so the
 * form stays focused and uncluttered.
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div className={styles.shell}>
      <aside className={styles.brand}>
        <div className={styles.brandTop}>
          <Logo size={30} />
        </div>

        <div className={styles.brandBody}>
          <p className="u-eyebrow">Control plane</p>
          <h1 className={styles.headline}>
            Bring your <em>Slack</em> and your <em>coding agents</em> into one room.
          </h1>
          <p className={styles.lede}>
            Cogent bridges a Slack channel to your Claude Code or Codex sessions — your
            team and your agents collaborating in real time, on your terms.
          </p>

          <ul className={styles.points}>
            <li>
              <span className={styles.dot} />
              <span>Least-privilege Slack app — reads only the channels you map.</span>
            </li>
            <li>
              <span className={styles.dot} />
              <span>Per-org isolation, tiered quotas, encrypted at rest.</span>
            </li>
            <li>
              <span className={styles.dot} />
              <span>One <code>/cogent map</code> and you're live.</span>
            </li>
          </ul>
        </div>

        <div className={styles.brandFoot}>
          Essential AI Solutions Ltd · <a href="https://portal.cogent.tools/terms">Terms</a> ·{" "}
          <a href="https://portal.cogent.tools/privacy">Privacy</a>
        </div>

        <div className={styles.grain} aria-hidden="true" />
      </aside>

      <main className={styles.panel}>
        <div className={styles.panelInner}>{children}</div>
      </main>
    </div>
  );
}
